import { deepEqual, isNill } from '@awork/_shared/functions/lodash'
import type { SyncFilter } from '@awork/core/state/signal-store/types'
import type { PersistState } from './persistState'

/**
 * Coerces the given value to an array
 * @param {T | T[]} value
 * @returns {T[]}
 */
export function coerceArray<T>(value: T | T[]): T[] {
  if (isNill(value)) {
    return []
  }

  return Array.isArray(value) ? value : [value]
}

/**
 * Checks if the given value is a function
 * @param {unknown} value
 * @returns {value is Function}
 */
export function isFunction(value: unknown): value is Function {
  return typeof value === 'function'
}

/**
 * Checks if the given value is an object
 * @param {unknown} value
 * @returns {value is object}
 */
export function isObject(value: unknown): value is object {
  const type = typeof value
  return value !== null && type === 'object'
}

/**
 * Checks if the given array is empty
 * @param {T} arr
 * @returns {boolean}
 */
export function isEmpty<T>(arr: T): boolean {
  if (isArray(arr)) {
    return arr.length === 0
  }

  return false
}

/**
 * Checks if the given value is an array
 * @param {unknown} value
 * @returns {value is T[]}
 */
export function isArray<T>(value: unknown): value is T[] {
  return Array.isArray(value)
}

export function areArraysEqual<T>(prevCollection: T[], currentCollection: T[]): boolean {
  if (prevCollection === currentCollection) {
    return true
  }

  if (!isArray(prevCollection) || !isArray(currentCollection)) {
    return false
  }

  if (isEmpty(prevCollection) && isEmpty(currentCollection)) {
    return true
  }

  if (prevCollection.length !== currentCollection.length) {
    return false
  }

  const isOneOfItemChanged = currentCollection.some((item, i) => {
    return !deepEqual(item, prevCollection[i])
  })

  // return false means there is a change and we want to call next()
  return isOneOfItemChanged === false
}

/**
 * Filters and maps the given array
 * @param {T[]} array
 * @param {(item: T) => boolean} filter
 * @param {(item: T) => T} map
 * @returns {T[]}
 */
export function filterAndMap<T>(array: T[], filter: (item: T) => boolean, map: (item: T) => T): T[] {
  return array.reduce((acc: T[], item: T) => {
    if (filter(item)) {
      acc.push(map(item))
    }

    return acc
  }, [])
}

/**
 * Removes unwanted properties from an entity.
 * @param {T} entity The entity from which properties will be removed.
 * @param {string[]} avoidProperties The list of properties to avoid.
 * @returns {T} A new entity with the unwanted properties removed.
 */
export function removeUnwantedProperties<T extends object>(entity: T, avoidProperties: string[]): T {
  const newEntity = {} as T

  for (const key in entity) {
    if (!avoidProperties.includes(key)) {
      newEntity[key] = entity[key]
    }
  }

  return newEntity
}

/**
 * Adds the given item or items to the array
 * @param {T[]} array
 * @param {T | T[]} newItem
 * @returns {T[]}
 */
export function arrayAdd<T>(array: T[], newItem: T | T[]): T[] {
  const newItems = coerceArray(newItem)
  const toArray = array || []

  return [...toArray, ...newItems]
}

export function arrayUpdate<T>(array: T[], idToUpdate: string | string[] | T, updatedValue: Partial<T>): T[]
export function arrayUpdate<T>(array: T[], predicate: (item: T) => boolean, updatedValue: Partial<T>): T[]
export function arrayUpdate<T>(
  array: T[],
  idToUpdateOrPredicate: string | string[] | T | ((item: T) => boolean),
  updatedValue: Partial<T>
): T[] {
  if (isFunction(idToUpdateOrPredicate)) {
    return array.map(item => {
      if (idToUpdateOrPredicate(item)) {
        return { ...item, ...updatedValue }
      }

      return item
    })
  }

  const ids = coerceArray(idToUpdateOrPredicate)

  return array.map(item => {
    if (ids.includes(isObject(item) ? item['id'] : item)) {
      return { ...item, ...updatedValue }
    }

    return item
  })
}

export function arrayUpsert<T>(
  array: T[],
  idToUpsert: string | string[] | T,
  updatedValue: Partial<T>,
  idProperty: keyof T = 'id' as keyof T
): T[] {
  const foundIndex = array.findIndex(item => (isObject(item) ? item[idProperty] === idToUpsert : item === idToUpsert))

  if (foundIndex !== -1) {
    const newArray = [...array]

    newArray[foundIndex] = { ...newArray[foundIndex], ...updatedValue }

    return newArray
  }

  return [...array, updatedValue as T]
}

/**
 * Removes the given item or items from the array
 * @param {T[]} array
 * @param {string | string[] | T} idToRemove
 * @returns {T[]}
 */
export function arrayRemove<T>(array: T[], idToRemove: string | string[] | T): T[]
export function arrayRemove<T>(array: T[], predicate: (item: T) => boolean): T[]
export function arrayRemove<T>(array: T[], idToRemoveOrPredicate: string | string[] | T | ((item: T) => boolean)): T[] {
  if (isFunction(idToRemoveOrPredicate)) {
    return array.filter(item => !idToRemoveOrPredicate(item))
  }

  const ids = coerceArray(idToRemoveOrPredicate)
  const filterFn = (current: T) => {
    return ids.includes(isObject(current) ? current['id'] : current) === false
  }

  if (Array.isArray(array)) {
    return array.filter(filterFn)
  }

  return []
}

/**
 * Returns an object with nullable properties set to undefined
 * @param {Partial<T>} entity
 * @param {(keyof T)[]} nullableProps
 * @returns {Partial<T>}
 */
export function getNullableOverrides<T>(entity: Partial<T>, nullableProps: (keyof T)[]): Partial<T> {
  if (!nullableProps?.length) {
    return {}
  }

  const nullableOverrides: Partial<T> = {}

  nullableProps.forEach(prop => {
    if (entity[prop] === undefined) {
      nullableOverrides[prop] = undefined
    }
  })

  return nullableOverrides
}

/**
 * Normalizes a date-like value into a valid `Date`.
 * @param {Date | string | null} value
 * @returns {Date | null}
 */
export function normalizeDateValue(value?: Date | string | null): Date | null {
  if (!value) {
    return null
  }

  const normalizedDate = new Date(value)

  return isNaN(normalizedDate.getTime()) ? null : normalizedDate
}

/**
 * Returns the latest available sync date.
 * This prevents a recovery full fetch from moving the sync cursor backwards when the full response
 * is empty or older than the current cursor.
 * @param {Date | null} currentCursor
 * @param {Date | null} latestEntityUpdate
 * @returns {Date | null}
 */
export function getLatestSyncDate(currentCursor: Date | null, latestEntityUpdate: Date | null): Date | null {
  if (!currentCursor) {
    return latestEntityUpdate
  }

  if (!latestEntityUpdate) {
    return currentCursor
  }

  return currentCursor.getTime() >= latestEntityUpdate.getTime() ? currentCursor : latestEntityUpdate
}

/**
 * Normalizes sync-filter metadata dates.
 * @param {SyncFilter[]} syncFilters
 * @returns {SyncFilter[]}
 */
export function normalizeSyncFilters(syncFilters?: SyncFilter[]): SyncFilter[] {
  return (syncFilters || []).map(syncFilter => ({
    ...syncFilter,
    lastSync: normalizeDateValue(syncFilter.lastSync)
  }))
}

/**
 * Checks whether the given value exposes all required method names.
 * @param {unknown} value
 * @param {string[]} methodNames
 * @returns {boolean}
 */
function hasObjectMethods(value: unknown, methodNames: string[]): boolean {
  if (!isObject(value)) {
    return false
  }

  const record = value as Record<string, unknown>

  return methodNames.every(methodName => typeof record[methodName] === 'function')
}

/**
 * Checks whether a value exposes the full persistence adapter API required by signal stores.
 * @param {unknown} storage
 * @returns {storage is PersistState}
 */
export function isPersistStateStorage(storage: unknown): storage is PersistState {
  return hasObjectMethods(storage, [
    'getItem',
    'applyPlainStateChanges',
    'getEntityState',
    'applyEntityChanges',
    'deleteItem',
    'deleteEntityStore'
  ])
}

/**
 * Checks whether a value exposes the plain-store delete API used during reset.
 * @param {unknown} storage
 * @returns {storage is Pick<PersistState, 'deleteItem'>}
 */
export function isPersistStatePlainDeleteStorage(storage: unknown): storage is Pick<PersistState, 'deleteItem'> {
  return hasObjectMethods(storage, ['deleteItem'])
}

/**
 * Checks whether a value exposes the entity-store delete API used during reset.
 * @param {unknown} storage
 * @returns {storage is Pick<PersistState, 'deleteEntityStore'>}
 */
export function isPersistStateEntityDeleteStorage(
  storage: unknown
): storage is Pick<PersistState, 'deleteEntityStore'> {
  return hasObjectMethods(storage, ['deleteEntityStore'])
}

/**
 * Checks whether a value exposes the bulk delete API used by maintenance commands.
 * @param {unknown} storage
 * @returns {storage is Pick<PersistState, 'deleteItems' | 'deleteAllItems'>}
 */
export function isPersistStateBulkDeleteStorage(
  storage: unknown
): storage is Pick<PersistState, 'deleteItems' | 'deleteAllItems'> {
  return hasObjectMethods(storage, ['deleteItems', 'deleteAllItems'])
}
